"use client"

import { Button } from "@/components/ui/button"
import { ArrowRight, Leaf, Star, BookOpen, Search, Loader2, ChevronDown, Calendar, Clock } from "lucide-react"
import { BlogGlassCard } from "@/components/blog/blog-glass-card"
import { BlogCardSkeleton } from "@/components/ui/blog-card-skeleton"
import { BlogErrorFallback } from "@/components/ui/blog-error-fallback"
import { ErrorBoundary } from "@/components/ui/error-boundary"
import { useContentPagination } from "@/hooks/use-content-pagination"
import { MotionConfig } from "motion/react"
import { Reveal } from "@/components/motion"
import { useInView } from "react-intersection-observer"
import { toast } from "sonner"
import Link from "next/link"
import Image from "next/image"
import { useState, useEffect, useCallback, useRef, useMemo, Suspense } from "react"
import React from "react"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { useSearchParams } from "next/navigation"
import { analytics, trackEvent } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { LIST_IMPRESSION_THRESHOLD } from "@/components/analytics/content-module-impression"
import { sanitizeSearchQuery } from "@/lib/search-query-telemetry"
import { getCategoryColor, PRIMARY_BLOG_CATEGORIES } from "@/lib/category-utils"
import { getCanonicalImageUrl } from "@/lib/directus"
import { resolveBlogImageAlt } from "@/lib/blog-image-alt"
import { generateBlurDataURL, directusImageLoader } from "@/lib/image-utils"
import { blogListingCount, blogListingCountLabel } from "@/lib/blog-listing-count"

interface BlogPost {
  id: string
  title: string
  excerpt: string
  category: string
  date?: string
  date_published?: string
  image: string
  image_alt?: string | null
  featured?: boolean
  slug?: string
  reading_time?: number
}

interface BlogListingClientProps {
  initialPosts?: BlogPost[]
  initialCategories?: string[]
  initialCategoryCounts?: Record<string, number>
  initialTotalCount?: number
}

export function BlogListingClient({ initialPosts = [], initialCategories, initialCategoryCounts = {}, initialTotalCount = 0 }: BlogListingClientProps) {
  const [moreOpen, setMoreOpen] = useState(false)
  const moreRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!moreOpen) return
    const onClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMoreOpen(false) }
    document.addEventListener("mousedown", onClick)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onClick)
      document.removeEventListener("keydown", onKey)
    }
  }, [moreOpen])


  // Error handler for toast notifications
  const handleError = useCallback((error: Error, context: string) => {
    const isNetworkError = error.message?.toLowerCase().includes('fetch') || 
                          error.message?.toLowerCase().includes('network')
    
    toast.error(isNetworkError ? 'Connection Problem' : 'Error Loading Content', {
      description: isNetworkError
        ? 'Please check your internet connection and try again.'
        : `Failed to ${context}. Please try again.`,
      duration: 6000,
      position: 'top-right',
      closeButton: true,
      action: {
        label: 'Retry',
        onClick: () => {
          // Retry will be handled by the hook
          window.location.reload()
        },
      },
    })
  }, [])
  
  // Filter/sort state. Initialized to plain defaults — NOT from useSearchParams().
  // That hook here deopted the whole /blog Suspense prerender to the empty
  // fallback shell (crawlers saw 0 post anchors), which forced the route
  // dynamic. URL deep-links are applied post-hydration by <SearchParamsSync>
  // below, an isolated null-fallback Suspense island, so the crawlable listing
  // stays in the static HTML and back/forward resyncs for free.
  const validSorts = ['newest', 'oldest', 'reading_time_asc', 'reading_time_desc'] as const
  type SortOption = typeof validSorts[number]
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const validReadingTimes = ['quick', 'medium', 'long'] as const
  const [readingTimeFilter, setReadingTimeFilter] = useState<'quick' | 'medium' | 'long' | null>(null)
  const validDateRanges = ['week', 'month', 'year', 'all'] as const
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'year' | 'all'>('all')
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>(initialCategoryCounts)
  const [totalPostsCount, setTotalPostsCount] = useState<number>(initialTotalCount)
  const [pendingRecoveryCategory, setPendingRecoveryCategory] = useState<string | null>(null)

  // Update URL params when filters change
  const updateURLParams = useCallback((updates: {
    sort?: string
    readingTime?: string | null
    dateRange?: string
    search?: string | null
  }) => {
    // Read the live URL at call time (no useSearchParams — see note above).
    const params = new URLSearchParams(window.location.search)
    
    if (updates.sort !== undefined) {
      if (updates.sort === 'newest') {
        params.delete('sort')
      } else {
        params.set('sort', updates.sort)
      }
    }
    
    if (updates.readingTime !== undefined) {
      if (updates.readingTime === null) {
        params.delete('readingTime')
      } else {
        params.set('readingTime', updates.readingTime)
      }
    }
    
    if (updates.dateRange !== undefined) {
      if (updates.dateRange === 'all') {
        params.delete('dateRange')
      } else {
        params.set('dateRange', updates.dateRange)
      }
    }
    
    if (updates.search !== undefined) {
      if (updates.search === null || updates.search === '') {
        params.delete('search')
      } else {
        params.set('search', updates.search)
      }
    }
    
    // history.pushState instead of router.push: Next 14.1+ syncs
    // useSearchParams/usePathname with native History API calls, and this
    // skips the RSC server round-trip a router.push would issue per filter
    // click. Preserves back/forward entries like router.push did.
    const qs = params.toString()
    window.history.pushState(null, '', qs ? `/blog?${qs}` : '/blog')
  }, [])

  // Request deduplication cache
  const fetchCacheRef = useRef<Map<string, { promise: Promise<any>, timestamp: number }>>(new Map())
  const CACHE_TTL = 5000 // 5 seconds cache TTL

  // Fetch blog posts from Directus - memoized to prevent infinite loops
  const fetchBlogPosts = useCallback(async (options?: {
    category?: string
    search?: string
    limit?: number
    offset?: number
  }) => {
    try {
      const params = new URLSearchParams()
      if (options?.category) params.append('category', options.category)
      if (options?.search) params.append('search', options.search)
      if (options?.limit) params.append('limit', options.limit.toString())
      if (options?.offset) params.append('offset', options.offset.toString())
      // Active search is ranked by relevance on the client. Do not let a prior
      // date sort choose a different 100-result candidate window.
      if (!options?.search && sortBy !== 'newest') params.append('sortBy', sortBy)
      if (readingTimeFilter) params.append('readingTimeFilter', readingTimeFilter)
      if (dateRange !== 'all') params.append('dateRange', dateRange)

      const cacheKey = params.toString()
      const now = Date.now()
      
      // Check cache for duplicate requests
      const cached = fetchCacheRef.current.get(cacheKey)
      if (cached && (now - cached.timestamp) < CACHE_TTL) {
        const result = await cached.promise
        // Update total count if this is a query for all posts (no filters)
        if (!options?.category && !options?.search) {
          setTotalPostsCount(result.total || 0)
        }
        return result.posts || []
      }

      // Create new request
      const promise = (async () => {
        const response = await fetch(`/api/directus/blog?${params}`)
        if (!response.ok) throw new Error('Failed to fetch blog posts')
        
        const data = await response.json()
        const result = {
          posts: data.posts || [],
          total: data.total || 0
        }
        // Update total count if this is a query for all posts (no filters)
        if (!options?.category && !options?.search) {
          setTotalPostsCount(result.total || 0)
        }
        return result
      })()

      // Cache the promise
      fetchCacheRef.current.set(cacheKey, { promise, timestamp: now })
      
      // Clean up old cache entries
      for (const [key, value] of fetchCacheRef.current.entries()) {
        if (now - value.timestamp > CACHE_TTL) {
          fetchCacheRef.current.delete(key)
        }
      }

      const result = await promise
      return result.posts || []
    } catch (error) {
      console.error('Error fetching blog posts:', error)
      throw error // Re-throw to let retry mechanism handle it
    }
  }, [sortBy, readingTimeFilter, dateRange])

  // Fetch categories and counts from Directus (only needed if initialCategories not provided)
  // Since we already have categories from server, this is mainly for category counts
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch('/api/directus/blog/categories')
      if (!response.ok) throw new Error('Failed to fetch categories')
      
      const data = await response.json()
      if (data.categoryCounts) {
        setCategoryCounts(data.categoryCounts)
      }
      // Return initialCategories if we have them (already loaded from server)
      return data.categories || initialCategories || []
    } catch (error) {
      console.error('Error fetching categories:', error)
      return initialCategories || []
    }
  }, [initialCategories])

  // Use the shared pagination hook with initial data from server
  const {
    items: blogPosts,
    categories,
    selectedCategory,
    searchTerm,
    isSearching,
    visibleItems: visiblePosts,
    isLoadingMore,
    isLoading,
    isLoadingCategories,
    hasMoreItems: hasMorePosts,
    errorState,
    handleSearch: handleSearchFromHook,
    handleCategoryFilter,
    handleQueryChange,
    handleLoadMore,
    retryLoadMore,
    clearFilters,
    setCategories,
    retry
  } = useContentPagination<BlogPost>({
    fetchItems: fetchBlogPosts,
    fetchCategories,
    initialItems: initialPosts,
    initialCategories: initialCategories,
    initialLimit: 12,
    loadMoreLimit: 12,
    searchLimit: 100,
    onError: handleError,
    maxRetries: 3,
    refreshKey: `${sortBy}|${readingTimeFilter ?? 'all'}|${dateRange}`,
  })

  // Prefetch buffer widened to ~1.5-2 viewports so a fast fling starts the next
  // load well before reaching the seam. Auto-load is driven from the inView
  // boolean in an effect (not the onChange callback) so pagination continues while
  // the sentinel stays in view instead of stalling until the next scroll.
  const { ref: sentinelRef, inView: sentinelInView } = useInView({
    rootMargin: "1500px",
  })
  // In-flight latch + a tick that re-arms the effect. The latch is released on the
  // next animation frame — NOT on an isLoadingMore transition — because the shared
  // hook's buffered-reveal branch is SYNCHRONOUS (setIsLoadingMore true->false
  // batches to a no-op; e.g. after a category/sort/search fetch of 25 items reveals
  // 12), so an isLoadingMore-keyed reset would strand the latch forever and kill
  // infinite scroll on filtered views. The rAF also lets the IntersectionObserver
  // re-evaluate inView before the next fire, and the tick bump re-runs this effect
  // so it auto-chains when the sentinel stays in view. The isLoadingMore guard
  // covers the async network window; the latch covers the synchronous same-tick
  // double-invoke (StrictMode / rapid churn).
  const loadMoreInFlightRef = useRef(false)
  const [loadMoreTick, setLoadMoreTick] = useState(0)
  useEffect(() => {
    if (
      !sentinelInView ||
      !hasMorePosts ||
      isLoadingMore ||
      isLoading ||
      errorState.context === 'load-more' ||
      loadMoreInFlightRef.current
    ) {
      return
    }
    loadMoreInFlightRef.current = true
    handleLoadMore()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        loadMoreInFlightRef.current = false
        setLoadMoreTick((t) => t + 1)
      })
    })
  }, [sentinelInView, hasMorePosts, isLoadingMore, isLoading, errorState.context, handleLoadMore, loadMoreTick])


  // Apply URL params to filter state. Fired by <SearchParamsSync> on mount and
  // on every URL change (deep links, back/forward, our own pushState). Setting
  // identical values is a React bail-out no-op, so self-triggered echoes from
  // updateURLParams are free; search is ref-guarded because handleSearchFromHook
  // kicks off a fetch even for an unchanged term.
  const searchTermRef = useRef(searchTerm)
  useEffect(() => {
    searchTermRef.current = searchTerm
  }, [searchTerm])
  const applyUrlParams = useCallback((params: URLSearchParams) => {
    const sort = params.get('sort')
    setSortBy(validSorts.includes(sort as SortOption) ? (sort as SortOption) : 'newest')
    const rt = params.get('readingTime')
    setReadingTimeFilter(validReadingTimes.includes(rt as any) ? (rt as 'quick' | 'medium' | 'long') : null)
    const dr = params.get('dateRange')
    setDateRange(validDateRanges.includes(dr as any) ? (dr as 'week' | 'month' | 'year' | 'all') : 'all')
    const urlSearch = params.get('search') || ''
    if (urlSearch !== searchTermRef.current) {
      handleSearchFromHook(urlSearch)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSearchFromHook])

  // Initialize with server-side data on mount
  useEffect(() => {
    if (initialCategories && initialCategories.length > 0) {
      setCategories(initialCategories)
    }
  }, [initialCategories, setCategories])

  // Use server data for initial render only when no filter is active. Once the
  // user types in search or picks a category, an empty hook result means
  // "no matches" — falling back to initialPosts here would show the SSR list
  // as if nothing happened, masking the empty state.
  const hasActiveFilter =
    Boolean(searchTerm) ||
    selectedCategory !== "All" ||
    Boolean(readingTimeFilter) ||
    dateRange !== "all"
  const displayPosts =
    hasActiveFilter || blogPosts.length > 0 ? blogPosts : initialPosts
  const displayedPostCount = blogListingCount({
    hasActiveFilter,
    loadedFilteredCount: displayPosts.length,
    totalCount: totalPostsCount,
  })
  const displayedPostCountLabel = blogListingCountLabel(displayedPostCount, hasActiveFilter)
  const displayCategories = useMemo(
    () => (categories.length > 0 ? categories : (initialCategories || [])),
    [categories, initialCategories]
  )

  // Debounced URL update for search
  const searchUrlUpdateRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const updateURLParamsRef = useRef(updateURLParams)
  
  // Keep ref updated
  useEffect(() => {
    updateURLParamsRef.current = updateURLParams
  }, [updateURLParams])
  
  // Update URL when search term changes (debounced). Track whether a search
  // was ever active so we skip the mount-time no-op but still clear ?search=
  // from the URL when the user erases an active query.
  const hadSearchRef = useRef(false)
  useEffect(() => {
    if (searchTerm) hadSearchRef.current = true
    if (!searchTerm && !hadSearchRef.current) return

    if (searchUrlUpdateRef.current) {
      clearTimeout(searchUrlUpdateRef.current)
    }
    
    searchUrlUpdateRef.current = setTimeout(() => {
      // Skip when the URL already carries this exact search (the term came
      // FROM the URL — deep link or back/forward). Writing it again would
      // push a duplicate history entry and truncate forward history.
      const urlSearch = new URLSearchParams(window.location.search).get('search') || ''
      if (urlSearch === (searchTerm || '')) return
      updateURLParamsRef.current({ search: searchTerm || null })
    }, 800) // Debounce URL updates for search (longer delay to reduce re-renders)
    
    return () => {
      if (searchUrlUpdateRef.current) {
        clearTimeout(searchUrlUpdateRef.current)
      }
    }
  }, [searchTerm]) // Removed updateURLParams from deps
  
  // Track blog_search once per settled query (debounced, non-empty)
  const searchTrackTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)
  useEffect(() => {
    if (searchTrackTimerRef.current) clearTimeout(searchTrackTimerRef.current)
    if (!searchTerm) return
    // Fire after search settles (isSearching has gone false and results are available)
    searchTrackTimerRef.current = setTimeout(() => {
      if (!isSearching && searchTerm) {
        // The term itself is the whole point of the event: without it a
        // zero-result search tells us nothing we can act on. Sanitized through
        // the shared gate (emails + long digit runs redacted, capped, lowered).
        const query = sanitizeSearchQuery(searchTerm)
        trackEvent('blog_search', {
          ...(query ? { query } : {}),
          query_length: searchTerm.length,
          result_count: displayPosts.length,
        })
      }
    }, 800)
    return () => {
      if (searchTrackTimerRef.current) clearTimeout(searchTrackTimerRef.current)
    }
  }, [searchTerm, isSearching, displayPosts.length])

  // Wrapper for handleSearch that also updates URL
  const handleSearch = useCallback((value: string) => {
    handleSearchFromHook(value)
    // URL update is handled by the useEffect watching searchTerm
  }, [handleSearchFromHook])
  
  // Wrapped category filter that also fires analytics
  const handleCategoryFilterTracked = useCallback((cat: string) => {
    handleCategoryFilter(cat)
    trackEvent('blog_category_filtered', { category: cat })
  }, [handleCategoryFilter])

  const handleRecoveryCategory = useCallback((cat: string) => {
    setSortBy('newest')
    setReadingTimeFilter(null)
    setDateRange('all')
    setPendingRecoveryCategory(cat)
    updateURLParams({ sort: 'newest', readingTime: null, dateRange: 'all', search: null })
  }, [updateURLParams])

  // Wait for the reset filter state to commit so fetchItemsRef points at a
  // callback with no stale reading-time, date, or sort constraints.
  useEffect(() => {
    if (!pendingRecoveryCategory) return
    if (sortBy !== 'newest' || readingTimeFilter !== null || dateRange !== 'all') return

    const category = pendingRecoveryCategory
    setPendingRecoveryCategory(null)
    void handleQueryChange(category, '')
    trackEvent('blog_category_filtered', { category })
  }, [dateRange, handleQueryChange, pendingRecoveryCategory, readingTimeFilter, sortBy])

  // Handle sort change
  const handleSortChange = useCallback((sort: 'newest' | 'oldest' | 'reading_time_asc' | 'reading_time_desc') => {
    setSortBy(sort)
    updateURLParams({ sort })
    // Refetch will be triggered by useEffect watching sortBy
  }, [updateURLParams])

  // Handle reading time filter change
  const handleReadingTimeFilterChange = useCallback((filter: 'quick' | 'medium' | 'long' | null) => {
    setReadingTimeFilter(filter)
    updateURLParams({ readingTime: filter })
    // Refetch will be triggered by useEffect watching readingTimeFilter
  }, [updateURLParams])

  // Handle date range change
  const handleDateRangeChange = useCallback((range: 'week' | 'month' | 'year' | 'all') => {
    setDateRange(range)
    updateURLParams({ dateRange: range })
    // Refetch will be triggered by useEffect watching dateRange
  }, [updateURLParams])

  // Override clearFilters to also clear sort/filter states
  const handleClearFilters = useCallback(() => {
    setSortBy('newest')
    setReadingTimeFilter(null)
    setDateRange('all')
    updateURLParams({ sort: 'newest', readingTime: null, dateRange: 'all', search: null })
    clearFilters()
  }, [clearFilters, updateURLParams])

  // Get featured and regular posts from display posts
  const featuredPosts = useMemo(() => displayPosts.filter(post => post.featured), [displayPosts])
  const regularPosts = useMemo(() => displayPosts.filter(post => !post.featured), [displayPosts])
  const displayedRegularPosts = regularPosts.slice(0, visiblePosts)

  // Filter pills: split categories into primary (inline) and overflow ("More" dropdown).
  // Primary order is fixed per design (PRIMARY_BLOG_CATEGORIES). Anything else from
  // Directus, plus any primary categories that don't exist in Directus, are filtered out.
  const { primaryCategories, overflowCategories } = useMemo(() => {
    const known = new Set(displayCategories)
    const primary = (PRIMARY_BLOG_CATEGORIES as readonly string[]).filter(c => known.has(c))
    const primarySet = new Set(primary)
    const overflow = displayCategories.filter(c => !primarySet.has(c)).sort((a, b) => a.localeCompare(b))
    return { primaryCategories: primary, overflowCategories: overflow }
  }, [displayCategories])

  // Editor's Picks row: 1 hero + 2 stacked halves. Only shown when no filters/search are active.
  const editorPicks = useMemo(() => {
    const filtersActive = !!searchTerm || selectedCategory !== "All" || !!readingTimeFilter || dateRange !== 'all'
    if (filtersActive) return { hero: null, halves: [] as BlogPost[] }
    const sorted = [...displayPosts].sort((a, b) => {
      if (a.featured && !b.featured) return -1
      if (!a.featured && b.featured) return 1
      const dA = new Date(a.date_published || a.date || '').getTime()
      const dB = new Date(b.date_published || b.date || '').getTime()
      return dB - dA
    })
    return { hero: sorted[0] || null, halves: sorted.slice(1, 3) }
  }, [displayPosts, searchTerm, selectedCategory, readingTimeFilter, dateRange])

  // Posts to render in the standard grid: regular posts excluding any used in Editor's Picks.
  const editorPickIds = useMemo(() => {
    const ids = new Set<string>()
    if (editorPicks.hero) ids.add(editorPicks.hero.id)
    editorPicks.halves.forEach(p => ids.add(p.id))
    return ids
  }, [editorPicks])

  // Dedup by id as the final guard before render: offset-based pagination can
  // append a boundary post that's already in the list (e.g. when posts are
  // published and shift offsets), which would otherwise throw a duplicate-key
  // error in the grid below. Also drops anything already shown in Editor's Picks.
  const gridPosts = useMemo(() => {
    const seen = new Set<string>()
    return displayedRegularPosts.filter(p => {
      if (editorPickIds.has(p.id) || seen.has(p.id)) return false
      seen.add(p.id)
      return true
    })
  }, [displayedRegularPosts, editorPickIds])

  // Get popular articles in selected category (for "Popular in Category" section)
  const getPopularInCategory = useCallback(() => {
    if (selectedCategory === "All" || !selectedCategory) return []
    // Use all available posts (blogPosts from hook, fallback to initialPosts)
    const allPosts = blogPosts.length > 0 ? blogPosts : initialPosts
    const categoryPosts = allPosts.filter(post => post.category === selectedCategory)
    // Sort by featured first, then by reading time (shorter = more accessible), then by date
    const sorted = categoryPosts
      .sort((a, b) => {
        if (a.featured && !b.featured) return -1
        if (!a.featured && b.featured) return 1
        if (a.reading_time && b.reading_time && a.reading_time !== b.reading_time) {
          return a.reading_time - b.reading_time
        }
        const dateA = new Date(a.date_published || a.date || '').getTime()
        const dateB = new Date(b.date_published || b.date || '').getTime()
        return dateB - dateA
      })
    // Exclude already displayed posts and get top 3
    const displayedIds = new Set(displayPosts.map(p => p.id))
    return sorted.filter(post => !displayedIds.has(post.id)).slice(0, 3)
  }, [selectedCategory, blogPosts, initialPosts, displayPosts])

  const popularInCategory = useMemo(() => getPopularInCategory(), [getPopularInCategory])

  // Show "no results" message when appropriate
  const showNoResults = displayPosts.length === 0 && !isLoading && (searchTerm || selectedCategory !== "All" || readingTimeFilter || dateRange !== 'all')

  // Handler functions for active filters
  const handleRemoveCategory = () => {
    handleCategoryFilter("All")
  }

  const handleRemoveSort = () => {
    handleSortChange('newest')
  }

  const handleRemoveReadingTime = () => {
    handleReadingTimeFilterChange(null)
  }

  const handleRemoveDateRange = () => {
    handleDateRangeChange('all')
  }

  const handleRemoveSearch = () => {
    handleSearch("")
  }

  // Show error fallback if there's an error and no posts to display
  if (errorState.hasError && displayPosts.length === 0 && !isLoading) {
    return (
      <div className="px-4 md:px-6 lg:px-8">
        <BlogErrorFallback
          error={errorState.error}
          context={errorState.context}
          onRetry={retry}
          retryCount={errorState.retryCount}
        />
      </div>
    )
  }

  return (
    <MotionConfig reducedMotion="user">
    <ErrorBoundary>
      {/* URL-param synchronizer: the ONLY consumer of useSearchParams(), kept
          in its own null-fallback Suspense so its prerender deopt can never
          swallow the crawlable listing again (the original /blog empty-shell
          SEO bug). */}
      <Suspense fallback={null}>
        <SearchParamsSync onChange={applyUrlParams} />
      </Suspense>
      {/* Aria-live region for search results updates */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="search-results-announcement"
      >
        {isSearching && searchTerm && `Searching for "${searchTerm}"...`}
        {!isSearching && hasActiveFilter && displayPosts.length > 0 &&
          `${displayedPostCountLabel}${searchTerm ? ` for "${searchTerm}"` : ''}`}
        {!isSearching && hasActiveFilter && displayPosts.length === 0 &&
          `No articles match the current filters${searchTerm ? ` for "${searchTerm}"` : ''}`}
        {!isSearching && !hasActiveFilter && displayPosts.length > 0 &&
          `Showing ${Math.min(displayPosts.length, visiblePosts)} of ${totalPostsCount} articles`}
      </div>

      <div className="pb-24 md:pb-20">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
      <div>
        {/* Page header — title, article count, prominent glass search. */}
        <div className="flex flex-col gap-4 pb-6 pt-3 md:flex-row md:items-end md:justify-between md:gap-8 md:pb-7 md:pt-5">
          <div className="min-w-0">
            <h1
              className="font-editorial font-extrabold leading-tight tracking-[-0.03em] text-[#14241b]"
              style={{ fontSize: "clamp(2rem, 2.4vw + 1rem, 2.9rem)" }}
            >
              Lawn care tips &amp; resources
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#41514a] md:text-base">
              Expert advice and seasonal guides for your lawn.
              {hasActiveFilter
                ? <> {isSearching ? "Updating matching articles." : `${displayedPostCountLabel}.`}</>
                : totalPostsCount > 0 && displayCategories.length > 0
                  ? <> {totalPostsCount} articles across {displayCategories.length} topics, updated weekly.</>
                  : null}
            </p>
          </div>
          <div className="relative w-full md:w-[360px] md:flex-shrink-0">
            <Search className="absolute left-3.5 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-gray-400" />
            <label htmlFor="blog-search" className="sr-only">Search articles</label>
            <input
              id="blog-search"
              type="search"
              inputMode="search"
              placeholder={hasActiveFilter ? "Search within these results…" : totalPostsCount > 0 ? `Search ${totalPostsCount} articles…` : "Search articles…"}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="h-12 w-full rounded-[14px] border border-white/60 bg-white/60 pl-10 pr-3 text-base text-gray-900 shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_6px_16px_-10px_rgba(15,40,25,0.25)] backdrop-blur-md transition focus:border-green-300 focus:outline-hidden focus:ring-4 focus:ring-green-500/15 md:text-sm"
            />
          </div>
        </div>

        {/* Featured editorial hero — opens the archive with an editorial hook
            instead of a bare filter bar. 1 hero card + 2 stacked half cards.
            Pinned ABOVE the filter bar; only shown when no filters/search are
            active so search/category results aren't pushed below the fold. The
            hero post is featured-first, else most-recent (always renders). */}
        {editorPicks.hero && (
          <Reveal onMount as="section" className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500">
                Editor&apos;s picks
              </span>
              <span aria-hidden="true" className="flex-1 h-px bg-gray-200" />
            </div>
            {/* 2-col layout starts at lg (1024px), not md (768px). At md the
             * half-card title column drops to ~115px which truncates 3-line
             * headlines mid-word ("How Long Does Weed and Feed Ta..."). At lg
             * each half-card column is ~390px which fits 3-line titles cleanly. */}
            {/* data-module marks the listing grids for the delegated
             * content_link_clicked listener; the display:contents wrappers
             * below opt out of it because they already emit blog_card_clicked,
             * which stays the card-click event of record. */}
            <ListingCardGrid listId="editor_picks" count={1 + editorPicks.halves.length} className="grid gap-4 lg:gap-5 lg:grid-cols-[1.4fr_1fr] items-stretch">
              <div
                data-content-link-skip="blog_card_clicked"
                style={{ display: 'contents' }}
                onClick={() => trackEvent('blog_card_clicked', { slug: editorPicks.hero!.slug || editorPicks.hero!.id, position: 0, cta_location: BLOG_LISTING_CARD_CTA_LOCATION, list_id: 'editor_picks' })}
              >
                <EditorHeroCard post={editorPicks.hero} />
              </div>
              <div className="grid grid-rows-2 gap-4">
                {editorPicks.halves.map((p, i) => (
                  <div
                    key={p.id}
                    data-content-link-skip="blog_card_clicked"
                style={{ display: 'contents' }}
                    onClick={() => trackEvent('blog_card_clicked', { slug: p.slug || p.id, position: i + 1, cta_location: BLOG_LISTING_CARD_CTA_LOCATION, list_id: 'editor_picks' })}
                  >
                    <EditorHalfCard post={p} />
                  </div>
                ))}
              </div>
            </ListingCardGrid>
          </Reveal>
        )}

        {/* Filter bar — single row, primary categories visible, "More" overflow dropdown. */}
        <div className="mb-7 md:mb-8 relative" ref={moreRef}>
          <div className="flex items-center gap-2 overflow-x-auto xl:overflow-visible -mx-4 px-4 xl:mx-0 xl:px-0 scrollbar-hide pb-1 xl:pb-0">
            <button
              onClick={() => handleCategoryFilterTracked("All")}
              className={`flex-shrink-0 inline-flex min-h-[44px] items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition whitespace-nowrap ${
                selectedCategory === "All"
                  ? 'bg-gray-900 text-white border border-gray-900'
                  : 'bg-white/50 text-gray-700 border border-white/55 backdrop-blur-md hover:-translate-y-px hover:bg-white/70 hover:border-white/80 hover:text-gray-900'
              }`}
            >
              All
            </button>
            {primaryCategories.map((cat) => {
              const on = selectedCategory === cat
              return (
                <button
                  key={cat}
                  onClick={() => handleCategoryFilterTracked(on ? "All" : cat)}
                  className={`flex-shrink-0 inline-flex min-h-[44px] items-center gap-2 px-3.5 py-2.5 rounded-full text-sm font-semibold transition whitespace-nowrap ${
                    on
                      ? 'bg-gray-900 text-white border border-gray-900'
                      : 'bg-white/50 text-gray-700 border border-white/55 backdrop-blur-md hover:-translate-y-px hover:bg-white/70 hover:border-white/80 hover:text-gray-900'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: on ? '#fff' : getCategoryColor(cat) }}
                  />
                  {cat}
                </button>
              )
            })}
            {overflowCategories.length > 0 && (
              <div className="relative flex-shrink-0">
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={moreOpen}
                  onClick={() => setMoreOpen(v => !v)}
                  className={`inline-flex min-h-[44px] items-center gap-2 px-3.5 py-2.5 rounded-full text-sm font-semibold border whitespace-nowrap backdrop-blur-md transition ${
                    moreOpen ? 'bg-white/80 border-white/80 text-gray-900' : 'bg-white/50 border-white/55 text-gray-700 hover:-translate-y-px hover:bg-white/70 hover:border-white/80 hover:text-gray-900'
                  }`}
                >
                  More
                  <span className="px-1.5 py-px text-[11px] font-semibold text-gray-500 bg-black/[0.06] rounded-full">
                    {overflowCategories.length}
                  </span>
                  <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
                </button>
                {moreOpen && (
                  <div
                    role="menu"
                    className="absolute top-full right-0 mt-2 w-72 max-h-80 overflow-y-auto rounded-2xl border border-white/60 bg-white/85 p-1.5 shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_24px_50px_-18px_rgba(15,40,25,0.32)] backdrop-blur-xl z-30"
                  >
                    {overflowCategories.map(cat => {
                      const on = selectedCategory === cat
                      return (
                        <button
                          key={cat}
                          role="menuitem"
                          onClick={() => { handleCategoryFilterTracked(on ? "All" : cat); setMoreOpen(false) }}
                          className={`w-full flex min-h-[44px] items-center gap-2.5 text-left px-2.5 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                            on ? 'bg-green-500/14 text-green-800' : 'text-gray-800 hover:bg-green-500/10'
                          }`}
                        >
                          <span
                            aria-hidden="true"
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: getCategoryColor(cat) }}
                          />
                          {cat}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Right-edge fade — below xl — signals there's more to swipe past
              the cut-off chip. Sibling of the scroller (not a child) so it
              stays anchored to the viewport's right edge instead of scrolling
              horizontally with the chip content. Width is 64px (w-16) at tablet+
              to fully mask the partial pill (otherwise the dot-bullet of the
              next pill pokes through the 32px fade and reads as abrupt clipping
              rather than a scroll affordance); narrower (w-8) at mobile-sm
              where partial pills are smaller. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-0 right-0 bottom-0 w-8 sm:w-16 bg-linear-to-l from-white via-white/80 to-transparent xl:hidden"
          />
        </div>

        <div>
          <div className="w-full">

      {/* No Results Section - Enhanced with educational guidance */}
      {showNoResults && (
        <div className="mb-16 md:mb-20">
          <div className="ds-glass rounded-3xl p-8 md:p-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-green-100 rounded-full">
                <BookOpen className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">
              {searchTerm ? `No articles found for "${searchTerm}"` : "No articles match your filters"}
            </h2>
            <p className="text-gray-700 text-lg md:text-xl mb-8 max-w-2xl mx-auto leading-relaxed">
              {searchTerm 
                ? "We couldn't find any articles matching your search. Let's help you discover great content!"
                : "Try adjusting your filters to see more articles, or explore our popular content below."}
            </p>
            
            {/* Educational guidance cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto mb-8">
              <div className="text-left p-5 bg-white rounded-xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow">
                <h4 className="font-bold text-gray-900 mb-3 text-base flex items-center gap-2">
                  <span className="text-green-600" aria-hidden="true">💡</span>
                  Search Tips
                </h4>
                <ul className="text-sm text-gray-700 space-y-2 leading-relaxed">
                  <li>• Check your spelling</li>
                  <li>• Use more general terms</li>
                  <li>• Try different keywords</li>
                  <li>• Browse by category instead</li>
                </ul>
              </div>
              <div className="text-left p-5 bg-white rounded-xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow">
                <h4 className="font-bold text-gray-900 mb-3 text-base flex items-center gap-2">
                  <span className="text-green-600" aria-hidden="true">⭐</span>
                  Start Learning
                </h4>
                <ul className="space-y-2 text-sm leading-relaxed text-gray-700">
                  {([
                    { label: "Lawn Care Basics", href: "/blog/category/lawn-care-tips" },
                    { label: "Seasonal Guides", href: "/blog/category/seasonal-care" },
                    { label: "Problem Solving", href: "/blog/category/problem-solving" },
                    { label: "Beginner-Friendly Tips", search: "beginner" },
                  ] as { label: string; href?: string; search?: string }[]).map((item) => (
                    <li key={item.label}>
                      {item.href ? (
                        <Link
                          href={item.href}
                          className="text-gray-700 transition-colors hover:text-green-600 hover:underline"
                        >
                          • {item.label}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleSearch(item.search!)}
                          className="text-gray-700 text-left transition-colors hover:text-green-600 hover:underline"
                        >
                          • {item.label}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-left p-5 bg-white rounded-xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow">
                <h4 className="font-bold text-gray-900 mb-3 text-base flex items-center gap-2">
                  <span className="text-green-600" aria-hidden="true">📚</span>
                  Explore Categories
                </h4>
                <ul className="text-sm text-gray-700 space-y-2 leading-relaxed">
                  {displayCategories.slice(0, 4).map(category => (
                    <li key={category}>
                      <button
                        onClick={() => handleRecoveryCategory(category)}
                        className="min-h-[44px] hover:text-green-600 hover:underline transition-colors"
                      >
                        • {category}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleClearFilters}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-full font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Clear Filters & Show All Articles
              </Button>
              {searchTerm && (
                <Button
                  onClick={() => handleSearch("")}
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-green-600 hover:text-green-600 px-8 py-3 rounded-full font-semibold text-base transition-all duration-300"
                >
                  Clear Search
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2026 Products Guide lead magnet — only on the unfiltered index, so it doesn't crowd search results. */}
      {editorPicks.hero && (
        <Reveal onMount as="section" className="mb-8 md:mb-12">
          <ProductGuideCta source="blog_listing" variant="inline" className="my-0" />
        </Reveal>
      )}

      {/* Popular in Category Section - Show when filtering by category */}
      {popularInCategory.length > 0 && selectedCategory !== "All" && (
        <Reveal onMount className="mb-12 md:mb-16">
          <div className="mb-6 md:mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center leading-tight">
              <Star className="w-5 h-5 md:w-6 md:h-6 text-yellow-500 mr-2 md:mr-3 flex-shrink-0" />
              <span>Popular in {selectedCategory}</span>
            </h2>
            <p className="text-gray-600 mt-2 text-sm md:text-base">
              Top articles to get you started in this category
            </p>
          </div>
          <ListingCardGrid listId="popular_in_category" count={popularInCategory.length} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {popularInCategory.map((post, index) => (
              <div key={post.id} data-content-link-skip="blog_card_clicked"
                style={{ display: 'contents' }} onClick={() => trackEvent('blog_card_clicked', { slug: post.slug || post.id, position: index, cta_location: BLOG_LISTING_CARD_CTA_LOCATION, list_id: 'popular_in_category' })}>
                <BlogGlassCard
                  post={{
                    id: post.id,
                    title: post.title,
                    excerpt: post.excerpt,
                    category: post.category,
                    date_published: post.date_published,
                    image: post.image,
                    image_alt: post.image_alt,
                    featured: post.featured,
                    slug: post.slug,
                    reading_time: post.reading_time
                  }}
                  searchTerm={searchTerm}
                />
              </div>
            ))}
          </ListingCardGrid>
        </Reveal>
      )}

      </div>
        </div>
      </div>

      {/* Cards Section - Full width */}
      <div style={{ contain: 'layout' }}>
        <div className="max-w-6xl mx-auto">
          <div className="w-full">
            {/* All Articles heading + count. Search lives in the page header above. */}
            <Reveal
              onMount
              className="mb-4 md:mb-6 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2"
            >
              <h2
                className="font-editorial flex items-center font-extrabold tracking-[-0.022em] text-[#14241b]"
                style={{ fontSize: "clamp(1.3rem, 1.2vw + 1rem, 1.75rem)" }}
              >
                {searchTerm ? "Search results" : selectedCategory === "All" ? hasActiveFilter ? "Filtered articles" : "All articles" : `${selectedCategory}`}
              </h2>
              <div className="flex items-center gap-3">
                {!isLoading && (
                  <span className="text-sm text-[#6b7a73]">
                    {displayedPostCountLabel}
                  </span>
                )}
                {searchTerm ? (
                  <span className="inline-flex h-10 items-center rounded-xl border border-white/60 bg-white/55 px-3.5 text-sm font-semibold text-gray-600 backdrop-blur-md">
                    Sorted by relevance
                  </span>
                ) : (
                  <div className="relative">
                    <select
                      aria-label="Sort articles"
                      value={sortBy}
                      onChange={(e) => handleSortChange(e.target.value as 'newest' | 'oldest' | 'reading_time_asc' | 'reading_time_desc')}
                      className="h-10 cursor-pointer appearance-none rounded-xl border border-white/60 bg-white/55 pl-3.5 pr-9 text-sm font-semibold text-gray-800 backdrop-blur-md transition focus:border-green-300 focus:outline-hidden"
                    >
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                      <option value="reading_time_asc">Quickest reads</option>
                      <option value="reading_time_desc">Longest reads</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  </div>
                )}
              </div>
            </Reveal>

            {/* Card Grid */}
            <div className="mb-8 md:mb-12">
        {isLoading && gridPosts.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 md:gap-x-6 lg:gap-x-7 gap-y-6 md:gap-y-7">
            {Array.from({ length: 6 }).map((_, index) => (
              <BlogCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        ) : (
          // Grid re-lays-out on every filter/sort/search swap. A container-level
          // opacity dip during an in-flight search cushions that reflow without
          // touching layout or double-animating the per-card Reveal onMount.
          // Global reduced-motion CSS zeroes the transition.
          <ListingCardGrid listId="all_posts" count={gridPosts.length} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 md:gap-x-6 lg:gap-x-7 gap-y-6 md:gap-y-7 transition-opacity duration-200 ${isSearching ? 'opacity-60' : 'opacity-100'}`}>
            {gridPosts.map((post, index) => (
              <React.Fragment key={post.id}>
                {index < 6 ? (
                  <Reveal onMount delay={Math.min(index * 0.04, 0.2)}>
                    {/* display:contents wrapper is layout-transparent; captures card clicks for analytics */}
                    <div data-content-link-skip="blog_card_clicked"
                style={{ display: 'contents' }} onClick={() => trackEvent('blog_card_clicked', { slug: post.slug || post.id, position: index, cta_location: BLOG_LISTING_CARD_CTA_LOCATION, list_id: 'all_posts' })}>
                      <BlogGlassCard
                        post={{
                          id: post.id,
                          title: post.title,
                          excerpt: post.excerpt,
                          category: post.category,
                          date_published: post.date_published,
                          image: post.image,
                          image_alt: post.image_alt,
                          featured: post.featured,
                          slug: post.slug,
                          reading_time: post.reading_time
                        }}
                        searchTerm={searchTerm}
                        imagePriority={index < 6}
                      />
                    </div>
                  </Reveal>
                ) : (
                  <div data-content-link-skip="blog_card_clicked"
                style={{ display: 'contents' }} onClick={() => trackEvent('blog_card_clicked', { slug: post.slug || post.id, position: index, cta_location: BLOG_LISTING_CARD_CTA_LOCATION, list_id: 'all_posts' })}>
                    <BlogGlassCard
                      post={{
                        id: post.id,
                        title: post.title,
                        excerpt: post.excerpt,
                        category: post.category,
                        date_published: post.date_published,
                        image: post.image,
                        image_alt: post.image_alt,
                        featured: post.featured,
                        slug: post.slug,
                        reading_time: post.reading_time
                      }}
                      searchTerm={searchTerm}
                      imagePriority={index < 6}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </ListingCardGrid>
        )}
        
        {/* Infinite scroll sentinel with error recovery */}
        {!isLoading && hasMorePosts && (
          <div
            ref={errorState.context === 'load-more' ? undefined : sentinelRef}
            className="flex min-h-[72px] items-center justify-center py-6 md:min-h-[88px] md:py-8"
          >
            {errorState.context === 'load-more' ? (
              <div className="flex flex-col items-center gap-3 text-sm text-gray-600">
                <span>Something went wrong loading more articles.</span>
                <Button
                  onClick={retryLoadMore}
                  variant="outline"
                  className="text-green-600 border-green-600 hover:bg-green-50"
                >
                  Try Again
                </Button>
              </div>
            ) : isLoadingMore ? (
              <div className="flex items-center gap-2 text-sm text-gray-600" role="status" aria-live="polite">
                <Loader2 className="h-5 w-5 animate-spin text-green-600" />
                <span>Loading more articles...</span>
              </div>
            ) : null}
          </div>
        )}
      </div>

          </div>
        </div>
      </div>
      </div>
      </div>
      </ErrorBoundary>
    </MotionConfig>
  )
}

// ────────────────────────────────────────────────────────────────────
// Suspense-isolated URL watcher. useSearchParams() lives ONLY here so the
// dynamic-API bailout is confined to this null-rendering island. Fires
// onChange with the current params on mount and on every URL change
// (pushState from our filters, browser back/forward, client navigations).
const BLOG_LISTING_CARD_CTA_LOCATION = 'blog_listing_card'

/**
 * A listing grid with one viewport-gated impression, keyed like the
 * `blog_card_clicked` rows it pairs with (`cta_location` + `list_id`). One per
 * grid rather than per card: per-card CTR comes from `position` on the click.
 * `data-module` stays so non-card anchors still reach the delegated
 * `content_link_clicked` listener.
 */
function ListingCardGrid({
  listId,
  count,
  className,
  children,
}: {
  listId: string
  count: number
  className?: string
  children: React.ReactNode
}) {
  const ref = useImpressionEvent<HTMLDivElement>(
    BLOG_LISTING_CARD_CTA_LOCATION,
    { surface: 'blog_listing', list_id: listId, count },
    { emit: analytics.ctaSurfaceShownWhenVisible, threshold: LIST_IMPRESSION_THRESHOLD },
  )
  return (
    <div ref={ref} data-module="listing_card" className={className}>
      {children}
    </div>
  )
}

function SearchParamsSync({ onChange }: { onChange: (params: URLSearchParams) => void }) {
  const searchParams = useSearchParams()
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])
  useEffect(() => {
    onChangeRef.current(new URLSearchParams(searchParams.toString()))
  }, [searchParams])
  return null
}

// ────────────────────────────────────────────────────────────────────
// Editor's Picks cards. Whole-card link, foreground titles, hover lift,
// thin top border in the category color.
function formatPickDate(date?: string): string {
  if (!date) return ''
  try {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  } catch {
    return ''
  }
}

// "New" badge fires for posts published within the last 14 days. Gated on a
// mounted flag: with ISR the server HTML can sit in cache up to 24h while the
// client clock moves on, so computing freshness during SSR and again at
// hydration can disagree near the 14-day boundary -> React hydration #418.
// Mirrors the canonical gate in components/ui/blog-card.tsx. Returns false until
// mounted (badge is simply absent on the SSR/first-paint frame, then appears).
function useIsNew(date?: string): boolean {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  if (!mounted || !date) return false
  try {
    const days = (Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24)
    return days >= 0 && days <= 14
  } catch {
    return false
  }
}

function EditorHeroCard({ post }: { post: BlogPost }) {
  const color = getCategoryColor(post.category)
  const showNew = useIsNew(post.date_published || post.date)
  return (
    <Link
      href={`/blog/${post.slug || post.id}`}
      className="ds-glass group relative grid grid-cols-1 md:grid-cols-[1.15fr_1fr] overflow-hidden rounded-3xl transition duration-300 hover:-translate-y-1 focus-visible:outline-2 focus-visible:outline-green-500 focus-visible:outline-offset-2"
    >
      <div className="relative aspect-video md:aspect-auto md:min-h-[260px]">
        {post.image ? (
          <Image
            src={getCanonicalImageUrl(post.image, post.slug || post.title)}
            loader={directusImageLoader}
            alt={resolveBlogImageAlt(post)}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            placeholder="blur"
            blurDataURL={generateBlurDataURL(post.image)}
            className="object-cover group-hover:scale-[1.02] transition-transform duration-300"
            priority
          />
        ) : (
          <div className="w-full h-full bg-linear-to-br from-green-100 to-emerald-100 flex items-center justify-center">
            <Leaf className="w-10 h-10 text-green-600" />
          </div>
        )}
        {showNew && (
          <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/90 backdrop-blur-xs text-gray-800 text-[10px] md:text-[11px] font-semibold tracking-wide border border-gray-200 rounded-full shadow-xs">
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-green-600" />
            New
          </span>
        )}
        <span className="absolute top-4 right-4 inline-flex items-center px-2.5 py-1 text-[10px] md:text-[11px] font-bold uppercase tracking-wider text-gray-800 bg-white/90 backdrop-blur-xs border border-gray-200 rounded-full">
          {post.featured ? "★ Featured" : "Latest"}
        </span>
      </div>

      <div className="p-5 md:p-6 flex flex-col justify-center gap-2.5">
        <span className="inline-flex items-center gap-1.5 self-start text-xs font-semibold text-gray-600 tracking-wide">
          <span aria-hidden="true" className="w-2 h-2 rounded-full" style={{ background: color }} />
          {post.category}
        </span>
        <h3 className="text-lg md:text-xl lg:text-2xl font-extrabold leading-snug tracking-[-0.018em] text-[#14241b] group-hover:text-green-700 transition-colors line-clamp-3">
          {post.title}
        </h3>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            {formatPickDate(post.date_published || post.date)}
          </span>
          <span className="text-gray-300">•</span>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            {post.reading_time || 5} min read
          </span>
        </div>
        {post.excerpt && (
          <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 max-w-prose">
            {post.excerpt}
          </p>
        )}
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700">
          Read article
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  )
}

function EditorHalfCard({ post }: { post: BlogPost }) {
  const color = getCategoryColor(post.category)
  const showNew = useIsNew(post.date_published || post.date)
  return (
    <Link
      href={`/blog/${post.slug || post.id}`}
      className="ds-glass group relative grid grid-cols-[140px_1fr] md:grid-cols-[160px_1fr] overflow-hidden rounded-2xl transition duration-300 hover:-translate-y-1 focus-visible:outline-2 focus-visible:outline-green-500 focus-visible:outline-offset-2"
    >
      <div className="relative">
        {post.image ? (
          <Image
            src={getCanonicalImageUrl(post.image, post.slug || post.title)}
            loader={directusImageLoader}
            alt={resolveBlogImageAlt(post)}
            fill
            sizes="(max-width: 768px) 140px, 160px"
            placeholder="blur"
            blurDataURL={generateBlurDataURL(post.image)}
            className="object-cover group-hover:scale-[1.03] transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-linear-to-br from-green-100 to-emerald-100 flex items-center justify-center">
            <Leaf className="w-7 h-7 text-green-600" />
          </div>
        )}
        {showNew && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 bg-white/90 backdrop-blur-xs text-gray-800 text-[9px] font-semibold tracking-wide border border-gray-200 rounded-full shadow-xs">
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-green-600" />
            New
          </span>
        )}
      </div>
      <div className="p-3.5 md:p-4 flex flex-col justify-center gap-1.5">
        <span className="inline-flex items-center gap-1.5 self-start text-[11px] font-semibold text-gray-600">
          <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
          {post.category}
        </span>
        <h4 className="text-sm md:text-[15px] font-bold leading-snug text-[#14241b] group-hover:text-green-700 transition-colors line-clamp-3">
          {post.title}
        </h4>
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          <span>{formatPickDate(post.date_published || post.date)}</span>
          <span className="text-gray-300">•</span>
          <span>{post.reading_time || 5} min read</span>
        </div>
      </div>
    </Link>
  )
}
